import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { portalDb } from './portalClient';

/** HTTP status from a supabase.functions.invoke error, when it carried one. */
export function invokeStatus(error: unknown): number | undefined {
  const ctx = (error as { context?: { status?: unknown } } | null)?.context;
  return typeof ctx?.status === 'number' ? ctx.status : undefined;
}

export class PortalRateLimited extends Error {
  constructor() {
    super('Too many requests. Please try again later.');
    this.name = 'PortalRateLimited';
  }
}

/** Public: emails a sign-in link. The server always answers "ok" so an unknown address is not disclosed. */
export function useRequestPortalLink() {
  return useMutation({
    mutationKey: ['portal', 'request-link'],
    mutationFn: async (input: { slug: string; email: string }) => {
      const { error } = await supabase.functions.invoke('portal-request-link', {
        body: { slug: input.slug, email: input.email.trim() },
      });
      if (error) {
        if (invokeStatus(error) === 429) throw new PortalRateLimited();
        throw error;
      }
      return true;
    },
  });
}

export function useSendPortalMessage(companyId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['portal', companyId, 'send-message'],
    mutationFn: async (input: { message: string; jobId?: string | null }) => {
      const { data, error } = await supabase.functions.invoke('portal-send-message', {
        body: {
          company_id: companyId,
          job_id: input.jobId || undefined,
          message: input.message.trim(),
        },
      });
      if (error) {
        if (invokeStatus(error) === 429) throw new PortalRateLimited();
        throw error;
      }
      return data as { ok: boolean; id: string };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['portal', companyId] });
    },
  });
}

/** Mints (or reuses) the signature token for an estimate the customer owns. */
export function useIssueEstimateToken() {
  return useMutation({
    mutationKey: ['portal', 'issue-estimate-token'],
    mutationFn: async (estimateId: string): Promise<string> => {
      const { data, error } = await portalDb.rpc('portal_issue_estimate_token', { p_estimate_id: estimateId });
      if (error) throw error;
      if (typeof data !== 'string' || !data) throw new Error('No signing link was returned');
      return data;
    },
  });
}
